import { CURRENCY_DECIMALS, decimalsFor, type CurrencyRoundingMode } from '@/lib/shared-types';

/**
 * The `currencies` row as the API serialises it. `CurrencyInfo` in
 * `src/lib/shared-types` is the *client* view — it leaves `id`, `isActive` and
 * `sortOrder` optional because most consumers only ever read a code. The
 * platform admin edits the row itself, so every column is required here.
 */
export interface PlatformCurrency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  roundingMode: CurrencyRoundingMode;
  symbolFirst: boolean;
  isActive: boolean;
  sortOrder: number;
}

/**
 * `CurrencyRoundingMode` is exported from `src/lib/shared-types` as both a const
 * object and a type. Re-exported under a name that is unambiguously the type,
 * so screens can import it alongside values without a second import statement.
 */
export type CurrencyRoundingModeValue = CurrencyRoundingMode;

export const ROUNDING_MODES: CurrencyRoundingMode[] = ['HALF_UP', 'HALF_EVEN', 'UP', 'DOWN'];

/** What the form holds. Numbers stay strings so a half-typed value is legal. */
export interface CurrencyForm {
  code: string;
  name: string;
  symbol: string;
  decimals: string;
  roundingMode: CurrencyRoundingMode;
  symbolFirst: boolean;
  isActive: boolean;
  sortOrder: string;
}

export const BLANK_CURRENCY: CurrencyForm = {
  code: '',
  name: '',
  symbol: '',
  decimals: '2',
  roundingMode: 'HALF_UP',
  symbolFirst: true,
  isActive: true,
  sortOrder: '0',
};

export function formToState(currency: PlatformCurrency): CurrencyForm {
  return {
    code: currency.code,
    name: currency.name,
    symbol: currency.symbol,
    decimals: String(currency.decimals),
    roundingMode: currency.roundingMode,
    symbolFirst: currency.symbolFirst,
    isActive: currency.isActive,
    sortOrder: String(currency.sortOrder),
  };
}

export interface IsoVerdict {
  /** The ISO 4217 minor unit for this code (2 when the code is unlisted). */
  expected: number;
  /** False when the code is not in the ISO table at all — nothing to check against. */
  known: boolean;
  /** True when the row claims a minor unit ISO does not agree with. */
  mismatch: boolean;
}

/**
 * Compares a row's `decimals` against ISO 4217.
 *
 * This matters more than it looks. `formatCurrency` derives its fraction digits
 * from the ISO table, not from this column — so a row claiming KWD has 2 places
 * does not change what any screen prints, it just makes the *server's* rounding
 * disagree with the display and with every other platform's numbers. That is
 * why the disagreement is surfaced rather than silently reconciled.
 */
export function isoVerdict(code: string, decimals: number): IsoVerdict {
  const upper = code.trim().toUpperCase();
  const known = upper.length === 3 && upper in CURRENCY_DECIMALS;
  const expected = decimalsFor(upper);
  return {
    expected,
    known,
    mismatch: Number.isFinite(decimals) && decimals !== expected,
  };
}

/**
 * Renders an amount from the row's OWN symbol, placement and decimal places.
 *
 * Deliberately not `formatCurrency`: that answers "what will every screen
 * print", which is driven by the ISO table. This answers "what does this row
 * claim". Showing both next to each other is the whole point of the preview.
 */
export function formatAsDefined(
  amount: string,
  row: { symbol: string; decimals: number; symbolFirst: boolean },
  locale: string,
): string {
  const digits = Number.isFinite(row.decimals)
    ? Math.max(0, Math.min(6, Math.trunc(row.decimals)))
    : 2;
  const value = Number(amount);
  const body = new Intl.NumberFormat(locale.startsWith('ar') ? 'ar-SA' : 'en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(value) ? value : 0);
  const symbol = row.symbol || '';
  return row.symbolFirst ? `${symbol} ${body}`.trim() : `${body} ${symbol}`.trim();
}

/** `GET /currencies` answers a bare array; tolerate a paginated envelope too. */
export function currencyRows(payload: unknown): PlatformCurrency[] {
  if (Array.isArray(payload)) return payload as PlatformCurrency[];
  const envelope = payload as { data?: PlatformCurrency[] } | null;
  return envelope?.data ?? [];
}

/**
 * The new ordering after a row is dragged from one position to another.
 *
 * Pure, and separated from the drag machinery, because the interesting part is
 * not the gesture — it is the renumbering. Positions are rewritten from zero
 * across the whole list rather than patched around the moved row, which is what
 * repairs the ties the registry seeded with: EGP and SAR both shipped at
 * `sortOrder: 0`, so neither had a defined position and no relative move on top
 * of them had a defined answer either.
 *
 * Returns the rows in their new order, each carrying the position it now holds.
 * `from` or `to` out of range returns the list untouched, so a drop onto a row
 * that vanished under a concurrent reload is a no-op rather than a scramble.
 */
export function reorderCurrencies(
  rows: PlatformCurrency[],
  from: number,
  to: number,
): PlatformCurrency[] {
  if (from < 0 || to < 0 || from >= rows.length || to >= rows.length) return rows;
  const next = rows.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((row, index) => ({ ...row, sortOrder: index }));
}
